import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function hasInternalHistory(locationKey) {
  if (typeof window !== 'undefined') {
    const idx = window.history?.state?.idx;
    if (typeof idx === 'number') {
      return idx > 0;
    }
  }

  return locationKey !== 'default';
}

export default function PageBackButton({
  label = 'Back',
  to,
  fallbackTo = '/',
  preferHistory = true,
  onClick,
  className = '',
  compact = false,
  hideLabelOnMobile = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBack = hasInternalHistory(location.key);

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (preferHistory && canGoBack) {
      navigate(-1);
      return;
    }

    navigate(to || fallbackTo);
  };

  const labelClassName = hideLabelOnMobile ? 'hidden sm:inline' : '';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        'group inline-flex items-center gap-2 rounded-md border border-borderDefault',
        'bg-backgroundSurface/90 px-2.5 py-2 pr-4 text-sm font-medium text-textSecondary',
        'shadow-sm backdrop-blur-sm transition-all duration-200',
        'hover:-translate-y-px hover:border-primary/30 hover:text-textPrimary hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2',
        compact ? 'pr-2.5' : '',
        className,
      ].join(' ')}
      aria-label={label}
      title={compact || hideLabelOnMobile ? label : undefined}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        <ArrowLeft className="h-4 w-4" />
      </span>
      {!compact && <span className={labelClassName}>{label}</span>}
    </button>
  );
}
